import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';

// Layout
import Layout from './components/common/Layout';

// Pages
import Login from './pages/Auth/Login';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Apply from './pages/Auth/Apply';
import VerifyEmail from './pages/Auth/VerifyEmail';
import TeamInvite from './pages/TeamInvite';
import CatalogoContadorPublico from './pages/CatalogoContadorPublico';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import Referrals from './pages/Referrals';
import ReferralDetail from './pages/Referrals/ReferralDetail';
import Clientes from './pages/Clientes';
import ClienteFicha, { FichaInicio, FichaPestana } from './pages/Clientes/Ficha';
import Commissions from './pages/Commissions';
import PayoutHistory from './pages/Commissions/PayoutHistory';
import DocumentosFiscales from './pages/Commissions/DocumentosFiscales';
import Organization from './pages/Organization';
import Resources from './pages/Resources';
import CountryDashboard from './pages/CountryDashboard';
import CountryHubEditor from './pages/CountryHubEditor';
import CountryAnnouncements from './pages/CountryAnnouncements';
import CountrySuccessStories from './pages/CountrySuccessStories';
import AuditLog from './pages/AuditLog';
import TeamGranular from './pages/TeamGranular';
import UpgradeRequest from './pages/UpgradeRequest';
import CountryEvents from './pages/CountryEvents';
import ConsultorCotizaciones from './pages/ConsultorCotizaciones';
import ConsultorCotizacionEditor from './pages/ConsultorCotizaciones/Editor';
import ConsultorLevantamientos from './pages/ConsultorLevantamientos';
import ConsultorLevantamientoEditor from './pages/ConsultorLevantamientos/Editor';
import ConsultorMigracion from './pages/ConsultorMigracion';
import ConsultorConsultas from './pages/ConsultorConsultas';
import ConsultorConsultaDetail from './pages/ConsultorConsultas/Detail';
// Reventa cd-2b (docs/120 F8): margen propio del asesor + certificación por insignia.
import ReventaResumen from './pages/Reventa/Resumen';
import ReventaAprendizaje from './pages/Reventa/Aprendizaje';
import ReventaMargen from './pages/Reventa/Margen';
import ReventaCertificacion from './pages/Reventa/Certificacion';
import ReventaIngresos from './pages/Reventa/Ingresos';
import Seguridad from './pages/Seguridad';

// Portal cd-2b multipaís (docs/179): pantallas nuevas cargadas bajo demanda.
const Inicio = lazy(() => import('./pages/Inicio'));
const Equipo = lazy(() => import('./pages/Equipo'));
const EquipoPipeline = lazy(() => import('./pages/Equipo/Pipeline'));
const Invitacion = lazy(() => import('./pages/Invitacion'));
// Academy es la única pantalla que sigue en MUI: se carga aparte, con su tema (pages/Academy/TemaMui).
const Academy = lazy(() => import('./pages/Academy/TemaMui').then((m) => ({ default: m.AcademyConTema })));
const AcademyCourseEditor = lazy(() => import('./pages/Academy/TemaMui').then((m) => ({ default: m.CourseEditorConTema })));

const Cargando = () => (
  <div className="flex min-h-[40vh] items-center justify-center text-body-sm text-subtle" role="status">Cargando…</div>
);
const conCarga = (el) => <Suspense fallback={<Cargando />}>{el}</Suspense>;

// Redirect to country dashboard if country_manager, otherwise dashboard.
// Si el partner aún no completó email-verification + KYC + 2FA, lo mandamos
// a /onboarding antes que cualquier otra cosa.
const DefaultRedirect = () => {
  const { partner } = useAuthStore();
  if (partner?.needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }
  if (partner?.organizationType === 'country_manager') {
    return <Navigate to="/country" replace />;
  }
  if (partner?.organizationType === 'cd2b_consultant') {
    return <Navigate to="/inicio" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

// Guardia por tipo de partner y por onboarding. `ProtectedRoute` solo autentica: sin esto un
// partner que no es asesor cd-2b (p. ej. un country manager) abría por URL las pantallas de
// cotización, cartera y reventa, y uno con el onboarding a medias se saltaba la verificación y el
// KYC. La API sigue siendo la autoridad; esto evita pantallas que solo darían 403.
//   - `consultor`: exige que el partner sea un asesor cd-2b.
//   - `onboarding` (por defecto sí): manda a /onboarding mientras `needsOnboarding`.
const ORG_TIPO_CONSULTOR = 'cd2b_consultant';
const PartnerGuard = ({ consultor = false, onboarding = true, children }) => {
  const { partner } = useAuthStore();
  if (onboarding && partner?.needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }
  if (consultor && partner?.organizationType !== ORG_TIPO_CONSULTOR) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <div className="flex h-screen items-center justify-center text-body-sm text-subtle" role="status">Cargando…</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/apply" element={<Apply />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/team-invite/:token" element={<TeamInvite />} />
      <Route path="/catalogo-contador/:token" element={<CatalogoContadorPublico />} />
      {/* Invitación de un gerente de país (pública, docs/179) */}
      <Route path="/invitacion/:token" element={conCarga(<Invitacion />)} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DefaultRedirect />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="dashboard" element={<Dashboard />} />
        {/* Portal cd-2b multipaís (docs/179). «Mi equipo» lo restringe la API al gerente activo. */}
        <Route path="inicio" element={<PartnerGuard consultor>{conCarga(<Inicio />)}</PartnerGuard>} />
        <Route path="equipo" element={<PartnerGuard consultor>{conCarga(<Equipo />)}</PartnerGuard>} />
        <Route path="equipo/pipeline" element={<PartnerGuard consultor>{conCarga(<EquipoPipeline />)}</PartnerGuard>} />
        <Route path="country" element={<CountryDashboard />} />
        {/* El editor de página legacy se retiró (docs/179 F3): su reemplazo es el editor del hub. */}
        <Route path="page-editor" element={<Navigate to="/hub-editor" replace />} />
        <Route path="hub-editor" element={<CountryHubEditor />} />
        <Route path="announcements" element={<CountryAnnouncements />} />
        <Route path="success-stories" element={<CountrySuccessStories />} />
        <Route path="audit-log" element={<AuditLog />} />
        <Route path="team-granular" element={<TeamGranular />} />
        <Route path="upgrade-request" element={<UpgradeRequest />} />
        <Route path="events" element={<CountryEvents />} />
        <Route path="academy" element={conCarga(<Academy />)} />
        <Route path="academy/courses/new" element={conCarga(<AcademyCourseEditor />)} />
        <Route path="academy/courses/:id" element={conCarga(<AcademyCourseEditor />)} />
        <Route path="referrals" element={<Referrals />} />
        <Route path="referrals/:id" element={<ReferralDetail />} />
        {/* Cartera del asesor cd-2b: las EMPRESAS que atiende (docs/72). */}
        <Route path="clientes" element={<PartnerGuard consultor><Clientes /></PartnerGuard>} />
        {/* Ficha: cada pestaña es una subruta; `/clientes/:subId` (y el viejo `?tab=x`) redirige. */}
        <Route path="clientes/:subId" element={<PartnerGuard consultor><ClienteFicha /></PartnerGuard>}>
          <Route index element={<FichaInicio />} />
          <Route path=":tab" element={<FichaPestana />} />
        </Route>
        <Route path="commissions" element={<Commissions />} />
        <Route path="commissions/payouts" element={<PayoutHistory />} />
        <Route path="commissions/documentos" element={<DocumentosFiscales />} />
        {/* Levantamiento: las preguntas que dimensionan la venta, antes de cotizar */}
        <Route path="consultor/levantamientos" element={<PartnerGuard consultor><ConsultorLevantamientos /></PartnerGuard>} />
        <Route path="consultor/levantamientos/:id" element={<PartnerGuard consultor><ConsultorLevantamientoEditor /></PartnerGuard>} />
        {/* Cotizaciones cd-2b (consultor) */}
        <Route path="consultor/cotizaciones" element={<PartnerGuard consultor><ConsultorCotizaciones /></PartnerGuard>} />
        <Route path="consultor/cotizaciones/nueva" element={<PartnerGuard consultor><ConsultorCotizacionEditor /></PartnerGuard>} />
        <Route path="consultor/cotizaciones/:id" element={<PartnerGuard consultor><ConsultorCotizacionEditor /></PartnerGuard>} />
        {/* Migrar los datos del sistema anterior del cliente a su empresa (docs/71 P11) */}
        <Route path="consultor/cotizaciones/:id/migracion" element={<PartnerGuard consultor><ConsultorMigracion /></PartnerGuard>} />
        {/* Consultas de soporte de clientes (consultor cd-2b) */}
        <Route path="consultor/consultas" element={<PartnerGuard consultor><ConsultorConsultas /></PartnerGuard>} />
        <Route path="consultor/consultas/:id" element={<PartnerGuard consultor><ConsultorConsultaDetail /></PartnerGuard>} />
        {/* Reventa cd-2b (docs/120 F8): margen propio + certificación. "Mi aprendizaje" nunca se
            gatea; el resto muestra el banner de gating dentro de la propia pantalla si el asesor
            aún no tiene `programas.reventaCd2b.habilitado` — ver `useReventaHabilitada`. */}
        <Route path="reventa/resumen" element={<PartnerGuard consultor><ReventaResumen /></PartnerGuard>} />
        <Route path="reventa/aprendizaje" element={<PartnerGuard consultor onboarding={false}><ReventaAprendizaje /></PartnerGuard>} />
        <Route path="reventa/margen" element={<PartnerGuard consultor><ReventaMargen /></PartnerGuard>} />
        <Route path="reventa/certificacion" element={<PartnerGuard consultor><ReventaCertificacion /></PartnerGuard>} />
        <Route path="reventa/ingresos" element={<PartnerGuard consultor><ReventaIngresos /></PartnerGuard>} />
        <Route path="organization" element={<Organization />} />
        {/* Verificación en dos pasos: al asesor cd-2b la API se la exige para cotizar */}
        <Route path="seguridad" element={<Seguridad />} />
        <Route path="resources" element={<Resources />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
